// ProjectileSet is the game-side state for projectiles known to the
// player. Keyed by `${shipUUID}:${projectileId}`. Each entry is a
// constant-velocity straight-line entity; rendering position is computed
// closed-form from `origin + vel × ageTicks`. No 3-frame correction is
// needed (the math is exact and identical across retransmits).
//
// Iteration 1.13: a `recentlyStopped` tombstone Map tracks projectiles
// the server has explicitly killed (`projectile_stop` WebSocket frame).
// Tombstones are checked on every spawn ingest so a late-arriving
// retransmit of an already-killed projectile doesn't render. Retention
// = TOMBSTONE_RETENTION_TICKS (60 = ProjectileTTL). Older entries are
// GC'd in tick().

const TOMBSTONE_RETENTION_TICKS = 60

export class ProjectileSet {
  constructor() {
    this.byKey = new Map()
    this.recentlyStopped = new Map() // key → stopTick
  }

  // ingest is called for each (header, record) pair the game receives.
  // Idempotent: receiving the same projectile twice is a no-op. Iter
  // 1.13: also a no-op if the projectile has been tombstoned.
  ingest(header, record) {
    const key = `${header.shipUUID}:${record.projectileId}`
    if (this.recentlyStopped.has(key)) return
    if (this.byKey.has(key)) return
    this.byKey.set(key, {
      key,
      shipUUID: header.shipUUID,
      projectileId: record.projectileId,
      spriteId: record.spriteId,
      originX: header.worldOriginX,
      originY: header.worldOriginY,
      spawnTick: record.spawnTick,
      lifetimeTicks: record.lifetimeTicks,
      expireTick: record.spawnTick + record.lifetimeTicks,
      vx: record.vx,
      vy: record.vy,
      // Computed each render frame.
      worldX: header.worldOriginX,
      worldY: header.worldOriginY,
    })
  }

  // applyStop processes one `projectile_stop` WebSocket frame from the
  // server. Records a tombstone and evicts any matching live
  // projectile so it stops rendering immediately at the kill site.
  applyStop(record) {
    const key = `${record.ship_uuid}:${record.local_projectile_id}`
    this.recentlyStopped.set(key, record.stop_tick | 0)
    this.byKey.delete(key)
  }

  // tick is called once per render frame, after viewTick has advanced.
  // Updates each projectile's rendered position, removes expired ones,
  // and GCs old tombstones.
  tick(viewTick) {
    for (const [key, p] of this.byKey) {
      if (viewTick > p.expireTick) {
        this.byKey.delete(key)
        continue
      }
      const ageTicks = viewTick - p.spawnTick
      p.worldX = p.originX + p.vx * ageTicks
      p.worldY = p.originY + p.vy * ageTicks
    }
    for (const [key, stopTick] of this.recentlyStopped) {
      if (viewTick - stopTick > TOMBSTONE_RETENTION_TICKS) {
        this.recentlyStopped.delete(key)
      }
    }
  }

  values() {
    return this.byKey.values()
  }

  size() {
    return this.byKey.size
  }
}
